import {
  AccountActionsInterface,
  AccountActionErrors,
  AccountActionsRequest,
  AccountActionsRequestedCallback,
  AccountActionsResponse,
  ActionsRequestedPayload,
} from '@central-products/interface-account-actions';

import { getDefaultActionData } from './defaultData';
import { Action, EventsToActions, ConfigArguments, AccountActionsConfig, Jurisdiction, ActionFactory } from './types';
import { emptyCallback, getActionData, getConfig, nextTick } from './utils';

export class AccountActions extends AccountActionsInterface {
  private actions: Map<string, Action | ActionFactory> = new Map();
  private actionRequested: AccountActionsRequestedCallback = emptyCallback;
  private configArgs: ConfigArguments;

  constructor({ language, jurisdiction, env, regSource, isMobile, dcf }: AccountActionsConfig) {
    super();

    this.configArgs = {
      language: language || 'en-gb',
      jurisdiction: jurisdiction || Jurisdiction.COM,
      env: env || '',
      regSource,
      isMobile,
      dcf,
    };
  }

  public async init(): Promise<void> {
    return getConfig(this.configArgs.jurisdiction).then((mappings) => this.setEventsToActions(mappings.default));
  }

  public execute(request: AccountActionsRequest): Promise<AccountActionsResponse> {
    const { eventId, context } = request;
    const action = this.actions.get(eventId);
    const actionDetails = typeof action === 'function' ? action(this.configArgs, context) : action;

    if (!actionDetails) {
      return Promise.reject({ error: AccountActionErrors.INVALID_EVENT_ID });
    }

    const { actionId, actionType, pathToData, defaultData } = actionDetails;
    const payload: ActionsRequestedPayload = {
      actionId,
      actionType,
      context,
      data:
        getActionData(context, pathToData) ||
        getDefaultActionData({
          data: defaultData,
          config: this.configArgs,
          context,
        }),
    };

    nextTick(() => {
      this.actionRequested(payload);
    });

    return Promise.resolve({});
  }

  public onActionRequested(callback: AccountActionsRequestedCallback): () => void {
    this.actionRequested = callback;

    return () => emptyCallback;
  }

  private setEventsToActions(mappings: EventsToActions): void {
    Object.keys(mappings).forEach((key: string) => this.actions.set(key, mappings[key]));
  }
}
